import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import { useAnalytics } from "../../../firebase/hooks/useAnalytics";
import { useTheme } from "../../../hooks/useTheme";
// Import modular documentation components
import GettingStarted from "./docs/GettingStarted";
import DashboardTutorial from "./docs/DashboardTutorial";
import ChatInterface from "./docs/ChatInterface";
import UserManagement from "./docs/UserManagement";
import AdminPanel from "./docs/AdminPanel";
import Troubleshooting from "./docs/Troubleshooting";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import PromptGuide from "./docs/PromptGuide";

export default function Docs() {
  const { currentUser, hasRole } = useAuth();
  const isAdmin = Boolean(hasRole && hasRole("admin_panel.access"));
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView } = useAnalytics();
  // Applies the persisted light/dark class to <html>. Signed-in visitors get
  // it from the Sidebar too, but a signed-out one (e.g. from the signup form)
  // has no Sidebar here.
  useTheme();

  // The open section rides in the URL (?section=…) so other pages can link
  // straight to one - the signup form links to Getting Started.
  const [activeSection, setActiveSection] = useState(
    () =>
      new URLSearchParams(location.search).get("section") || "getting-started",
  );

  const openSection = (id) => {
    setActiveSection(id);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("section", id);
    navigate(
      { pathname: location.pathname, search: searchParams.toString() },
      { replace: true },
    );
  };

  const getLangFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);

    return params.get("lang") === "zh" ? "zh" : "en";
  }, [location.search]);

  const [language, setLanguage] = useState(getLangFromUrl);

  // Track page view when component mounts
  useEffect(() => {
    trackPageView("Documentation");
  }, [trackPageView]);

  // Function to change language and update URL
  const handleLanguageChange = (isEnglish) => {
    const newLang = isEnglish ? "en" : "zh";
    setLanguage(newLang);

    // Update URL with new language
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("lang", newLang);
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  };

  // Wrapper function for LanguageSwitcher component
  const handleLanguageSwitcherChange = (lang) => {
    setLanguage(lang);

    // Update URL with new language
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("lang", lang);
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  };

  // Keep language in sync with URL (if user pastes a new URL)
  useEffect(() => {
    setLanguage(getLangFromUrl());
  }, [getLangFromUrl]);

  const appVersion = "1.0.0";
  const lastUpdated = "2025-09-15";

  const navigationAfterLogin = [
    {
      id: "getting-started",
      title: language === "en" ? "Getting Started" : "開始使用",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      id: "dashboard",
      title: language === "en" ? "Dashboard Tutorial" : "儀表板教學",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "chat-interface",
      title: language === "en" ? "AI Chat Interface" : "AI 聊天介面",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
    },
    {
      id: "prompt-guide",
      title: language === "en" ? "Prompt Guide" : "提示指南",
      icon: (
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M8 13V16H16V8H8V9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            ></path>{" "}
            <path
              d="M16 16.001L19 16.0005C20.6569 16.0002 22.0002 17.3432 22.0005 19C22.0007 20.6569 20.6578 22.0002 19.0009 22.0005C17.3441 22.0007 16.0007 20.6578 16.0005 19.001L16 16.001Z"
              stroke="currentColor"
              strokeWidth="1.5"
            ></path>{" "}
            <path
              d="M8.00096 16.001L5.00096 16.0005C3.34411 16.0002 2.00075 17.3432 2.00049 19C2.00023 20.6569 3.34316 22.0002 5.00002 22.0005C6.65687 22.0007 8.00023 20.6578 8.00049 19.001L8.00096 16.001Z"
              stroke="currentColor"
              strokeWidth="1.5"
            ></path>{" "}
            <path
              d="M16 8.00002L19 8.00049C20.6569 8.00075 22.0002 6.65781 22.0005 5.00096C22.0007 3.34411 20.6578 2.00075 19.0009 2.00049C17.3441 2.00023 16.0007 3.34316 16.0005 5.00002L16 8.00002Z"
              stroke="currentColor"
              strokeWidth="1.5"
            ></path>{" "}
            <path
              d="M8.00096 8.00002L5.00096 8.00049C3.34411 8.00075 2.00075 6.65781 2.00049 5.00096C2.00023 3.34411 3.34316 2.00075 5.00002 2.00049C6.65687 2.00023 8.00023 3.34316 8.00049 5.00002L8.00096 8.00002Z"
              stroke="currentColor"
              strokeWidth="1.5"
            ></path>{" "}
          </g>
        </svg>
      ),
    },
    // Admin-only section
    ...(isAdmin
      ? [
          {
            id: "user-management",
            title: language === "en" ? "User Management" : "使用者管理",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                />
              </svg>
            ),
          },
          {
            id: "admin-panel",
            title: language === "en" ? "Admin Panel" : "管理者面板",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            ),
          },
        ]
      : []),
    {
      id: "troubleshooting",
      title: language === "en" ? "Troubleshooting" : "故障排除",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];
  const navigationItems = [
    {
      id: "getting-started",
      title: language === "en" ? "Getting Started" : "開始使用",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      id: "troubleshooting",
      title: language === "en" ? "Troubleshooting" : "故障排除",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "getting-started":
        return <GettingStarted isEN={language === "en"} />;
      case "dashboard":
        return <DashboardTutorial isEN={language === "en"} />;
      case "chat-interface":
        return <ChatInterface isEN={language === "en"} />;
      case "prompt-guide":
        return (
          <PromptGuide
            isEN={language === "en"}
            onLanguageChange={handleLanguageChange}
          />
        );
      case "user-management":
        if (!isAdmin) {
          return (
            <div className="text-center text-red-500">
              {language === "en"
                ? "Access Denied: Admins Only"
                : "拒絕訪問：僅限管理員"}
            </div>
          );
        }
        return <UserManagement isEN={language === "en"} />;
      case "admin-panel":
        if (currentUser && currentUser.role == "admin") {
          return (
            <div className="text-center text-red-500">
              {language === "en"
                ? "Access Denied: Admins Only"
                : "拒絕訪問：僅限管理員"}
            </div>
          );
        }
        return <AdminPanel isEN={language === "en"} />;
      case "troubleshooting":
        return <Troubleshooting isEN={language === "en"} />;
      default:
        return <GettingStarted isEN={language === "en"} />;
    }
  };

  return (
    <div className="h-screen bg-slate-100 dark:bg-dashboard-bg flex">
      {/* Header */}
      {currentUser && <Sidebar />}
      <LanguageSwitcher
        language={language}
        setLanguage={handleLanguageSwitcherChange}
      />
      <div className="flex-1 overflow-y-auto pt-16">
        <div className="">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex md:flex-row flex-col items-center space-x-4">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                  {language === "en" ? "Documentation" : "文件"}
                </h1>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  v{appVersion} • {language === "en" ? "Updated" : "更新"}{" "}
                  {lastUpdated}
                </div>
              </div>

              {!currentUser && (
                <button
                  onClick={() =>
                    navigate({
                      pathname: "/",
                      search: `?lang=${language}`,
                    })
                  }
                  className="flex flex-row gap-2 items-center justify-center border border-gray-200 dark:border-white/20 rounded-2xl md:p-4 px-1 py-2 bg-white dark:bg-white/10 backdrop-blur-sm shadow-lg text-gray-700 dark:text-white rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <div className="md:w-8 md:h-8 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center shadow-lg">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  {language === "en" ? "Back to Home" : "回首頁"}
                </button>
              )}
            </div>

            {!currentUser && (
              <div className="mx-auto w-full mt-4 mb-8">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 px-4 py-3 flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                      {language === "en"
                        ? "Limited Access Mode"
                        : "有限訪問模式"}
                    </p>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-center md:text-justify text-gray-700 dark:text-gray-300 mb-4">
                      {language === "en"
                        ? "You are viewing the documentation without being logged in. Some sections and features may be limited in this public version."
                        : "您正在未登入狀態下檢視文件。在此公開版本中，某些部分和功能可能受到限制。"}
                    </p>
                    <div className="flex md:flex-row flex-col gap-1 justify-between items-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {language === "en"
                          ? "For the best experience with full access"
                          : "為獲得最佳體驗和完整存取權限"}
                      </p>
                      <button
                        onClick={() =>
                          navigate({
                            pathname: "/login",
                            search: `?lang=${language}`,
                          })
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                          />
                        </svg>
                        {language === "en" ? "Sign In" : "登入"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Horizontal Navigation */}
            {!currentUser && (
              <div className="">
                <nav className="flex space-x-8 overflow-x-auto py-4">
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openSection(item.id)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        activeSection === item.id
                          ? "bg-orange-500 text-white"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {currentUser && (
              <div className="">
                <nav className="flex space-x-8 overflow-x-auto py-4">
                  {navigationAfterLogin.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => openSection(item.id)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        activeSection === item.id
                          ? "bg-orange-500 text-white"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Content. The text color is a default for the many doc elements
            that set none of their own, which would otherwise render black on
            the dark-mode cards. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-gray-700 dark:text-gray-300">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
